// apps/client/src/modules/Shortcuts/TabIndexHint.tsx
import { type ReactNode } from 'react'
import { useAtomValue } from 'jotai'
import * as PopperPrimitive from '@radix-ui/react-popper'
import { Portal } from '@radix-ui/react-portal'
import { Kbd } from '@duck/ui'
import { heldModifiersAtom, hintsSuppressedAtom } from './useHeldModifiers'

interface TabIndexHintProps {
  /** 1-based tab index. Only 1..9 receive hints. */
  index: number
  children: ReactNode
}

/**
 * Wraps a tab and floats its 1..9 index badge above it when the user holds
 * Alt alone. Portaled so it escapes the tab strip's `overflow-y: hidden`.
 */
export const TabIndexHint = ({ index, children }: TabIndexHintProps) => {
  const held = useAtomValue(heldModifiersAtom)
  const suppressed = useAtomValue(hintsSuppressedAtom)

  const visible =
    index >= 1 && index <= 9 && held.alt && !held.cmd && !held.shift && !suppressed

  return (
    <PopperPrimitive.Root>
      <PopperPrimitive.Anchor asChild>
        {children}
      </PopperPrimitive.Anchor>
      {visible && (
        <Portal>
          <PopperPrimitive.Content
            side="top"
            sideOffset={4}
            collisionPadding={8}
            aria-hidden
            className="pointer-events-none z-[5000]"
          >
            <Kbd>{index}</Kbd>
          </PopperPrimitive.Content>
        </Portal>
      )}
    </PopperPrimitive.Root>
  )
}
